import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Stack, TextField, Tooltip, Typography } from "@mui/material";
import AxisSeverityValueList from "./AxisSeverityValueList";
import ColorScalePicker from "./ColorScalePicker";
import type { AxisProperties } from "./api";
import t from "./translations";

function LabelWithHint({ label, hint }: { label: string; hint: string }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Tooltip title={hint}>
        <InfoOutlinedIcon
          fontSize="inherit"
          sx={{ color: "text.secondary" }}
        />
      </Tooltip>
    </Stack>
  );
}

const VALUE_MIN = 0;
const VALUE_MAX = 255;

const clampValue = (n: number) => Math.min(VALUE_MAX, Math.max(VALUE_MIN, n));

type Props = {
  region: string;
  value: AxisProperties;
  onChange: (value: AxisProperties) => void;
  disabled?: boolean;
  lowestMaxValue?: number;
};

// Pure field layout, shared by CreateAxisDialog and AxisDetailsDrawer - each
// owns its own state/save logic, this just renders the shared fields once.
// name_en is deliberately NOT here - callers render it themselves so the
// drawer can put its Save affordance right next to it, same as the
// Systems tab's edit drawer.
export default function AxisPropertiesFields({
  region,
  value,
  onChange,
  disabled,
  lowestMaxValue,
}: Props) {
  const maxBelowLowest =
    lowestMaxValue !== undefined && value.max_value < lowestMaxValue;
  const set = <K extends keyof AxisProperties>(
    key: K,
    fieldValue: AxisProperties[K]
  ) => onChange({ ...value, [key]: fieldValue });

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <LabelWithHint label={t.ValueRangeLabel} hint={t.ValueRangeHint} />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Tooltip title={t.MinValueLockedHint}>
            <span style={{ width: "100%" }}>
              <TextField
                label={t.MinValueLabel}
                type="number"
                value={value.min_value}
                disabled
                slotProps={{ htmlInput: { min: VALUE_MIN, max: VALUE_MAX } }}
                fullWidth
              />
            </span>
          </Tooltip>
          <Typography color="text.secondary">-</Typography>
          <TextField
            label={t.MaxValueLabel}
            type="number"
            value={value.max_value}
            onChange={(event) => {
              const max = clampValue(Number(event.target.value));
              set("max_value", Math.max(max, value.min_value));
            }}
            disabled={disabled}
            error={maxBelowLowest}
            slotProps={{
              htmlInput: {
                min: lowestMaxValue ?? VALUE_MIN,
                max: VALUE_MAX,
                "data-testid": "axis-properties.max",
              },
            }}
            fullWidth
          />
        </Stack>
        {lowestMaxValue !== undefined && (
          <Typography
            variant="caption"
            color={maxBelowLowest ? "error" : "text.secondary"}
          >
            {t.AxisInUseMaxHint(lowestMaxValue)}
          </Typography>
        )}
      </Stack>
      <Stack spacing={0.5}>
        <LabelWithHint label={t.ColorScaleLabel} hint={t.ColorScaleHint} />
        <ColorScalePicker
          region={region}
          min={value.min_value}
          max={value.max_value}
          value={value.color_scale}
          onChange={(colorScale) =>
            colorScale !== null && set("color_scale", colorScale)
          }
        />
      </Stack>
      <Stack spacing={0.5}>
        <LabelWithHint
          label={t.SeveritiesLabel}
          hint={t.SeveritiesDescription}
        />
        <AxisSeverityValueList
          region={region}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      </Stack>
    </Stack>
  );
}
